// Tests: npx tsx src/lib/utils/__tests__/units.test.ts

// ---------------------------------------------------------------------------
// Pluralization
// ---------------------------------------------------------------------------

const IRREGULAR_PLURALS: Record<string, string> = {
  // The product's word for any mix of video, image and audio, and its own plural: "3 media".
  media: 'media',
};

/**
 * Returns "1 file", "3 files", "1,234 files", etc.
 * Appends "s" by default; override with the irregulars dict above or pass an explicit plural.
 */
export function pluralize(count: number, singular: string, plural?: string): string {
  const p = plural ?? IRREGULAR_PLURALS[singular] ?? singular + 's';
  return `${formatNumber(count)} ${count === 1 ? singular : p}`;
}

// Use SI units for bytes conversion (not 1024-based binary units)
export const MB_TO_BYTES = 1_000_000;

// Displayed credits = API credits / CREDITS_API_TO_DISPLAY_SCALE.
// We currently display credits 1:1 with the API value; the constant remains
// so the formatter (and any "< threshold" defensive branch downstream)
// stays a no-op rather than removing the abstraction entirely.
//
// If you change this away from 1, one known place will need a second look —
// the rest of the codebase reads the scale through formatCredits and so
// already adapts automatically:
//
//   1. Backend product names with a "K" suffix (e.g. "Pro 300K"). At
//      scale=1000 those names display as "300K" but the user actually
//      gets only 300 display credits — `removeKFromProductName` in
//      `src/pages/Pricing/PricingPage.tsx` used to strip the trailing K
//      to compensate; it was removed when we moved to scale=1. Bring it
//      back if you raise the scale again.
//
// We also dropped the tests that exercised the "< threshold" branch of
// `formatCredits` with fractional API inputs, on the assumption that
// API credits are always integers. If that assumption ever breaks,
// restore the tests with: `git revert 0fe9ccf8 0ce5f4d1` (and delete
// this paragraph).
export const CREDITS_API_TO_DISPLAY_SCALE = 1;

// Time conversion constants
export const SECONDS_IN_HOUR = 3600;

export type UnitOptions = {
  detail?: number;
  precision?: number;
  /** Decimal places to round to before floor/ceil, to avoid floating-point artifacts (default 5). */
  decimalPlacesToRound?: number;
  spaceBeforeUnit?: boolean;
  suffix?: string;
  locale?: string;
  /** 'floor' / 'ceil' snap the leading-unit value to a whole number (the
   * default — gives "5 MB" hierarchical output, with sub-units carrying any
   * remainder when detail > 1). 'none' keeps the fractional part on the
   * leading unit, useful for rate-style displays like "5.2 MB/s". */
  roundType?: 'floor' | 'ceil' | 'none';
};

export function formatUnits(
  value: number,
  units: { name: string; factor: number }[],
  {
    detail = 1,
    precision = 1,
    decimalPlacesToRound = 5,
    spaceBeforeUnit = true,
    suffix = '',
    locale = 'en-US',
    roundType = 'floor',
  }: UnitOptions = {},
): string {
  const roundingFactor = 10 ** decimalPlacesToRound;

  // Handle negative values: format the absolute value and prepend "-".
  if (value < 0) {
    return (
      '-' +
      formatUnits(-value, units, {
        detail,
        precision,
        decimalPlacesToRound,
        spaceBeforeUnit,
        suffix,
        locale,
        roundType,
      })
    );
  }

  // Reverse the units so that we start with the largest unit.
  // (Assumes units are passed in ascending order.)
  const sortedUnits = units.slice().reverse();

  // Recursive helper to format the value.
  function recursiveFormat(val: number, remainingUnits: typeof sortedUnits, d: number): string[] {
    // Base case: no more details or no more units.
    if (d <= 0 || remainingUnits.length === 0) {
      return [];
    }

    const [current, ...rest] = remainingUnits;

    if (val >= current.factor) {
      const thisUnit = val / current.factor;
      // We round first so that if we have a value like 0.999999999, it will be rounded to 1.000000000 and then floored.
      const rounded = Math.round(thisUnit * roundingFactor) / roundingFactor;
      const thisUnitRounded =
        roundType === 'none'
          ? rounded
          : roundType === 'ceil'
            ? Math.ceil(rounded)
            : Math.floor(rounded);
      const remainder = val - thisUnitRounded * current.factor;
      // When at the smallest unit or when this is the final detail level, use fixed precision.
      const rawValue =
        rest.length === 0 || d === 1
          ? thisUnitRounded.toLocaleString(locale, {
              maximumFractionDigits: precision,
              minimumFractionDigits: 0,
            })
          : thisUnitRounded.toLocaleString(locale);
      // Remove trailing zeros after decimal point and unnecessary decimal point
      const cleanedValue = rawValue.replace(/\.0+$/, '');
      const formatted = `${cleanedValue}${spaceBeforeUnit ? ' ' : ''}${current.name}`;
      if (remainder === 0) {
        return [formatted];
      }
      return [formatted, ...recursiveFormat(remainder, rest, d - 1)];
    }

    // If the current unit doesn't fit, try the next (smaller) one.
    return recursiveFormat(val, rest, d);
  }

  const parts = recursiveFormat(value, sortedUnits, detail);

  // Fallback if no unit was applicable.
  if (parts.length === 0) {
    const cleanedValue = value
      .toLocaleString(locale, {
        maximumFractionDigits: precision,
        minimumFractionDigits: 0,
      })
      .replace(/\.0+$/, '');
    return `${cleanedValue}${spaceBeforeUnit ? ' ' : ''}${units[0].name}` + suffix;
  }

  return parts.join(' ') + suffix;
}

const mbToGbUnits = [
  { name: 'MB', factor: 1 },
  { name: 'GB', factor: 1000 },
  { name: 'TB', factor: 1000 * 1000 },
  { name: 'PB', factor: 1000 * 1000 * 1000 },
];
export function formatMbToString(value: number, options?: UnitOptions) {
  return formatUnits(value, mbToGbUnits, options);
}

// Uses SI units (1000-based, not 1024-based binary units)
// When converting to bytes, use factor of 1000 (e.g., 1 MB = 1,000,000 bytes)
const bytesToGbUnits = [
  { name: 'B', factor: 1 },
  { name: 'KB', factor: 1000 },
  { name: 'MB', factor: 1000 * 1000 },
  { name: 'GB', factor: 1000 * 1000 * 1000 },
  { name: 'TB', factor: 1000 * 1000 * 1000 * 1000 },
  { name: 'PB', factor: 1000 * 1000 * 1000 * 1000 * 1000 },
];
export function formatBytesToString(value: number, options?: UnitOptions) {
  return formatUnits(value, bytesToGbUnits, options);
}

/**
 * Format a bytes-per-second rate ("21.4 MB/s", "500 KB/s"). Picks the
 * largest unit ≥ 1 of the value's magnitude and keeps fractional precision
 * on the leading unit. Sub-KB rates are clamped to KB with 2 decimals
 * ("0.05 KB/s") so the unit doesn't flicker between KB and B as the rate
 * fluctuates near the boundary on a slow connection.
 */
export function formatBytesPerSec(bps: number): string {
  if (bps >= 1000) return `${formatBytesToString(bps, { roundType: 'none' })}/s`;
  return `${(bps / 1000).toFixed(2)} KB/s`;
}

const secToDurationUnits = [
  { name: 'sec', factor: 1 },
  { name: 'min', factor: 60 },
  { name: 'h', factor: 60 * 60 },
];
export function formatDurationToString(value: number, options?: UnitOptions) {
  if (!isFinite(value)) return '∞';
  if (value === 0) return '0 sec';
  // At whole-second precision a positive amount under a second floors to "0 sec", which reads as
  // nothing at all for a sub-second clip.
  const precision = value > 0 && value < 1 && options?.precision === 0 ? 2 : options?.precision;
  return formatUnits(value, secToDurationUnits, {
    ...options,
    precision,
    locale: options?.locale || 'en-US',
  });
}

export function formatDurationToHHMMSS(value: number) {
  const hours = Math.floor(value / 60 / 60);
  const minutes = Math.floor((value - hours * 60 * 60) / 60);
  const seconds = Math.floor(value % 60);
  let ret = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  if (hours > 0) {
    ret = `${hours}:${ret}`;
  }
  return ret;
}

/**
 * Whole seconds → a compact clock for UI copy: `1:30`, `12:05`, `1:02:03`.
 *
 * Deliberately not `formatDurationToHHMMSS`, which pads the minutes to `01:30`. That padding
 * is correct on a timeline, where every readout has to be the same width or the numbers jump
 * as they tick — and wrong in a sentence or a menu label, because nobody writes "meet me at
 * 01:30". Same value, different setting.
 *
 * Seconds are always padded; it's the leading field that isn't.
 */
export function formatClockLabel(value: number): string {
  const total = Math.max(0, Math.floor(value));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const mmss = `${minutes}:${seconds.toString().padStart(2, '0')}`;
  return hours > 0
    ? `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    : mmss;
}

/**
 * Format duration to HH:MM:SS, or return a placeholder for undefined values.
 * Provides consistent handling of optional durations across the application.
 * @param duration - Duration in seconds, or undefined
 * @returns Formatted duration string or '—' if undefined
 */
export function formatOptionalDuration(duration: number | undefined): string {
  if (duration === undefined) return '—';
  return formatDurationToHHMMSS(duration);
}

export function formatDateToLocale(dateString: string | number, locale: string = 'en-US') {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (error) {
    // If date parsing fails, return the original string
    return dateString;
  }
}

export function formatDate(timestamp: number, locale: string = 'en-US') {
  const date = new Date(timestamp);
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatCurrency(currency: string): string {
  return currency.toUpperCase() === 'USD' ? '$' : currency;
}

/**
 * Format API-scale credits for display. Use this (and roundMode) for all credits display;
 * do not use raw Math.round/Math.floor on credits elsewhere, or values can diverge (e.g. -1 vs -2).
 */
export function formatCredits(
  credits: number | null | undefined,
  roundMode?: 'up' | 'down',
): string {
  if (credits === null || credits === undefined) {
    return '0';
  }
  // Divide by CREDITS_API_TO_DISPLAY_SCALE to convert from API credits to display credits
  // (API returns credits that are CREDITS_API_TO_DISPLAY_SCALE times larger than what we display)
  const displayCredits = credits / CREDITS_API_TO_DISPLAY_SCALE;

  let roundedCredits: number;
  if (roundMode === 'up') {
    roundedCredits = Math.ceil(displayCredits);
  } else if (roundMode === 'down') {
    roundedCredits = Math.floor(displayCredits);
  } else {
    roundedCredits = Math.round(displayCredits);
  }

  // If rounded to 0 but original credits is not 0, show "< threshold" for
  // positive values. Negative values that round to 0 are negligible — just show "0".
  if (roundedCredits === 0 && credits !== 0) {
    if (credits < 0) {
      return '0';
    }

    // Find the smallest power of 10 threshold that's greater than displayCredits
    let threshold = 1;
    let decimalPlaces = 0;

    while (threshold > displayCredits && decimalPlaces < 10) {
      threshold = threshold / 10;
      decimalPlaces++;
    }

    // If threshold is now <= displayCredits, go back one step (multiply by 10)
    if (threshold <= displayCredits) {
      threshold = threshold * 10;
      decimalPlaces = Math.max(0, decimalPlaces - 1);
    }

    const thresholdStr = threshold.toFixed(decimalPlaces);
    return `< ${thresholdStr}`;
  }

  return roundedCredits.toLocaleString('en-US');
}

/**
 * formatCredits + the word "credit"/"credits" appended.
 * Singular only when the displayed value is exactly "1".
 * Optional modifier is placed between the number and unit (e.g. "5 more credits").
 */
export function formatCreditsWithUnit(
  credits: number | null | undefined,
  roundMode?: 'up' | 'down',
  modifier?: string,
): string {
  const formatted = formatCredits(credits, roundMode);
  const unit = formatted === '1' ? 'credit' : 'credits';
  return modifier ? `${formatted} ${modifier} ${unit}` : `${formatted} ${unit}`;
}

export function formatPrice(price: { currency: string; unit_amount: number }): string {
  return `${formatCurrency(price.currency)}${price.unit_amount.toLocaleString('en-US')}`;
}

export function formatNumber(value: number): string {
  return value.toLocaleString('en-US');
}

export function formatStorageHours(seconds: number, options?: UnitOptions): string {
  return formatDurationToString(seconds, {
    precision: 0,
    detail: 2,
    ...options,
  });
}

export interface FormatCostPercentageOptions {
  useDecimalThresholds?: boolean;
  decimalThresholdLimit?: number;
}

/**
 * Format cost as percentage of available resource
 * @param cost - The cost amount
 * @param current - The current available amount
 * @param resourceName - Name of the resource (e.g., "credits", "storage")
 * @param options - Formatting options
 * @returns Formatted string like "<1% credits", "≈25% credits", or "Not enough credits", or null if cost is invalid
 */
export function formatCostPercentage(
  cost: number | undefined,
  current: number | undefined,
  resourceName: string,
  options: FormatCostPercentageOptions = {},
): string | null {
  if (cost === undefined || cost <= 0) {
    return null;
  }

  if (current === undefined || current <= 0) {
    return `Not enough ${resourceName}`;
  }

  const { useDecimalThresholds = true, decimalThresholdLimit = Infinity } = options;
  const percentage = (cost / current) * 100;

  if (percentage > 100) {
    return `Not enough ${resourceName}`;
  }

  if (percentage < 1) {
    if (!useDecimalThresholds) {
      return `<1% ${resourceName}`;
    }

    // For values < 1%, show the threshold it's less than: <1%, <0.1%, <0.01%, etc.
    let threshold = 1;
    let decimalPlaces = 0;
    while (percentage < threshold && decimalPlaces < decimalThresholdLimit) {
      threshold = threshold / 10;
      decimalPlaces++;
    }
    // Format the threshold with appropriate decimal places
    const formatted = threshold.toFixed(decimalPlaces);
    return `<${formatted}% ${resourceName}`;
  }

  return `≈${Math.round(percentage)}% ${resourceName}`;
}

// ---------------------------------------------------------------------------
// Resource impact display (credits / storage "left after" vs "extra needed")
// ---------------------------------------------------------------------------
//
// Use these when showing impact of a cost (e.g. upload) on credits or storage.
//
// Typical flow (e.g. in ImpactPopup):
//   1. isResourceInsufficient(credits) / isResourceInsufficient(storage)
//      → whether the user has enough (false) or needs more (true).
//   2. getResourceImpactRowLabel({ resource: 'credits', isInsufficient })
//      → label: "Credits left after" or "Extra credits needed" (and storage equivalents).
//   3. getRemainingOrNeededAmount({ ctx: credits, isInsufficient })
//      → raw number: either remaining (left) or extra needed (cost − current).
//   4. Format that number (formatCredits, formatStorageHours, etc.).
//   5. If some inputs are unknown (e.g. missing duration), wrap with:
//      formatAsEstimatedMinimum({ formatted, uncertain }) for "cost" rows,
//      formatAsEstimatedRemainingOrNeeded({ formatted, uncertain, isInsufficient }) for "left/extra" rows.
//
// Inequality meaning when uncertain: cost/extra-needed are "at least" (≥), remaining is "at most" (≤).
// ---------------------------------------------------------------------------

/** Returns true if the string already starts with an inequality symbol (< ≤ > ≥). */
function startsWithInequality(s: string): boolean {
  return /^[<>≤≥]/.test(s);
}

interface EstimatedValue {
  formatted: string;
  uncertain: boolean;
  /** Prefix a certain value with "≈ ", for figures that are still a pre-upload estimate. */
  approximate?: boolean;
}

function prefixEstimate(formatted: string, prefix: string | undefined): string {
  if (!prefix || startsWithInequality(formatted)) return formatted;
  return `${prefix} ${formatted}`;
}

/**
 * Prefixes a formatted value with "≥ " when the estimate is uncertain (e.g. missing duration).
 * Use for "cost" rows: cost is a minimum, so we show "≥ X".
 * Skips the prefix if the value already starts with an inequality (e.g. "< 0.01").
 */
export function formatAsEstimatedMinimum({ formatted, uncertain, approximate }: EstimatedValue) {
  return prefixEstimate(formatted, uncertain ? '≥' : approximate ? '≈' : undefined);
}

/**
 * Prefixes a formatted value with "≤ " when the estimate is uncertain.
 * Use when the value is an upper bound (e.g. "storage left after" when duration is unknown).
 * Skips the prefix if the value already starts with an inequality (e.g. "< 0.01").
 */
export function formatAsEstimatedMaximum({ formatted, uncertain, approximate }: EstimatedValue) {
  return prefixEstimate(formatted, uncertain ? '≤' : approximate ? '≈' : undefined);
}

/**
 * For the "left after" / "extra needed" row: adds ≥ or ≤ when uncertain.
 * - Extra needed → minimum → "≥ {formatted}".
 * - Left after → maximum → "≤ {formatted}".
 */
export function formatAsEstimatedRemainingOrNeeded(args: {
  formatted: string;
  uncertain: boolean;
  isInsufficient: boolean;
}): string {
  const { formatted, uncertain, isInsufficient } = args;
  // No prefix when the estimate is exact or the value already has an inequality.
  if (!uncertain || startsWithInequality(formatted)) return formatted;
  // Extra needed = minimum → "≥"; left after = maximum → "≤".
  return isInsufficient ? `≥ ${formatted}` : `≤ ${formatted}`;
}

/**
 * Returns whether the user does not have enough resource for the cost.
 * Use for credits or storage when you have { cost, current }.
 *
 * @param ctx - { cost, current } or null/undefined if the resource is not shown.
 */
export function isResourceInsufficient(
  ctx: { cost: number; current: number } | null | undefined,
): boolean {
  return ctx != null && ctx.cost > ctx.current;
}

/**
 * Returns the label for the "left after" / "extra needed" row.
 * Use with the result of isResourceInsufficient for the same resource.
 */
export function getResourceImpactRowLabel(args: {
  resource: 'credits' | 'storage';
  isInsufficient: boolean;
}): string {
  const { resource, isInsufficient } = args;
  if (resource === 'credits') {
    return isInsufficient ? 'Extra credits needed' : 'Credits left after';
  }
  return isInsufficient ? 'Extra storage needed' : 'Storage left after';
}

/**
 * Returns the numeric value to display in the "left after" / "extra needed" row.
 * - When insufficient: "extra needed" = cost − current.
 * - When sufficient: "left after" = left.
 */
export function getRemainingOrNeededAmount(args: {
  ctx: { cost: number; current: number; left: number };
  isInsufficient: boolean;
}): number {
  const { ctx, isInsufficient } = args;
  return isInsufficient ? ctx.cost - ctx.current : ctx.left;
}
